import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  model,
  OnChanges,
  OnInit,
  output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';

import { translate } from '@jsverse/transloco';
import { debounceTime } from 'rxjs';

import { noWhitespaceValidator } from 'app/utils/customValidators';
import { AppSharedModule } from '../../../app-shared.module';

export interface FieldBucket {
  bucketName: string;
  minimumValue: number;
  includeMinimum: boolean;
  maximumValue: number;
  includeMaximum: boolean;
}

export interface BucketsForm {
  bucketName: FormControl<string>;
  minimumValue: FormControl<number>;
  includeMinimum: FormControl<boolean>;
  maximumValue: FormControl<number>;
  includeMaximum: FormControl<boolean>;
}
export interface FieldBucketForm {
  items: FieldBucketFormArray;
}

export type FieldBucketFormArray = FormArray<FieldFormGroup>;
export type FieldFormGroup = FormGroup<BucketsForm>;

export enum FieldBucketType {
  FLOAT = 'float',
  INTEGER = 'integer',
}

export interface FieldBucketsFormValue {
  valid: boolean;
  value: FieldBucket[];
}
@Component({
  selector: 'app-field-buckets',
  standalone: true,
  imports: [AppSharedModule],
  templateUrl: './field-buckets.component.html',
  styleUrl: './field-buckets.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldBucketsComponent implements OnInit, OnChanges {
  expectedType = input<FieldBucketType>();
  buckets = input<FieldBucket[]>();
  hasRadioSelect = input<boolean>(false);
  isReadOnly = input<boolean>(false);

  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);

  form!: FormGroup<FieldBucketForm>;

  readonly emptyBaseFormValue: FieldBucket = {
    bucketName: null!,
    minimumValue: null!,
    includeMinimum: false,
    maximumValue: null!,
    includeMaximum: false,
  };

  tableRows = signal<FieldFormGroup[]>([]);

  validationErrors = computed<string[]>(() => {
    this.formStatus();
    this.expectedType();
    this.updateErrorSignal();
    return this.formErrorState();
  });
  formErrorState = signal([]);

  formStatus = signal(0);

  fieldBuckets = output<FieldBucketsFormValue>();
  bucketSelectedIndex = output<number>();
  numericStep = computed(() => (this.expectedType() === FieldBucketType.FLOAT ? 0.1 : 1));
  selectedBucketIndex = model();

  get itemsFormArray() {
    return this.form?.controls?.items;
  }

  ngOnInit(): void {
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.buckets) {
      if (this.form) {
        const buckets = this.buckets();
        this.form.controls.items.clear();
        if (buckets) {
          buckets.forEach((bucket) => this.addRow(bucket));
        }
      }
    }
  }

  private initForm(): void {
    const defaultValue = this.buckets() || [];
    this.form = this.fb.group<FieldBucketForm>(
      {
        items: this.fb.array<FieldFormGroup>(defaultValue.map((b) => this.buildRowForm(b))),
      },
      { validators: [this.nameDuplicateValidator, this.rangeOverlapValidator] }
    );

    this.form.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.formStatus.update((val) => {
        this.updateTableAndErrors();
        return val + 1;
      });
    });

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(300))
      .subscribe(() => {
        this.fieldBuckets.emit({ valid: this.form.valid, value: this.form.getRawValue().items });
      });

    this.triggerUpdateOnTableAndErrors();
  }

  private triggerUpdateOnTableAndErrors() {
    this.formStatus.update((val) => {
      this.updateTableAndErrors();
      return val + 1;
    });
  }

  onAddItem() {
    this.addRow(this.emptyBaseFormValue);
  }

  private addRow(defaultvalue: FieldBucket) {
    this.itemsFormArray.push(this.buildRowForm(defaultvalue));
    this.triggerUpdateOnTableAndErrors();
  }

  onDeleteItem(index: number) {
    const currentSelectedIndex = this.selectedBucketIndex() as number | undefined;

    this.itemsFormArray.removeAt(index);

    // Handle selected bucket index after deletion
    if (this.itemsFormArray.length === 0) {
      // No rows left, clear selection
      this.selectedBucketIndex.set(undefined);
      this.bucketSelectedIndex.emit(undefined);
    } else if (currentSelectedIndex !== undefined && currentSelectedIndex !== null) {
      if (index === currentSelectedIndex) {
        // Deleted the selected row, clear selection
        this.selectedBucketIndex.set(undefined);
        this.bucketSelectedIndex.emit(undefined);
      } else if (index < currentSelectedIndex) {
        // Deleted a row before the selected one, adjust index
        this.selectedBucketIndex.set(currentSelectedIndex - 1);
        this.bucketSelectedIndex.emit(currentSelectedIndex - 1);
      }
      // If deleted row is after selected row, no change needed
    }

    this.triggerUpdateOnTableAndErrors();
  }

  private buildRowForm(initValue: FieldBucket): FormGroup<BucketsForm> {
    const newGroup = this.fb.group<BucketsForm>(
      {
        bucketName: this.fb.control(
          { value: initValue.bucketName, disabled: this.isReadOnly() },
          { validators: [Validators.required, Validators.maxLength(32), noWhitespaceValidator] }
        ),
        minimumValue: this.fb.control(
          { value: initValue.minimumValue, disabled: this.isReadOnly() },
          {
            validators: [Validators.required],
          }
        ),
        includeMinimum: this.fb.control(
          { value: initValue.includeMinimum, disabled: this.isReadOnly() },
          {
            validators: [Validators.required],
          }
        ),
        maximumValue: this.fb.control(
          { value: initValue.maximumValue, disabled: this.isReadOnly() },
          {
            validators: [Validators.required],
          }
        ),
        includeMaximum: this.fb.control(
          { value: initValue.includeMaximum, disabled: this.isReadOnly() },
          {
            validators: [Validators.required],
          }
        ),
      },
      { validators: [this.typeFormatValidator, this.minLessThanMaxValidator] }
    );
    return newGroup;
  }

  handleSelection(index) {
    this.selectedBucketIndex.set(index);
    this.bucketSelectedIndex.emit(index);
  }

  private updateTableAndErrors() {
    this.tableRows.set([...this.itemsFormArray?.controls]);
    this.updateErrorSignal();
  }

  private updateErrorSignal() {
    const rootErrors = this.form.errors;
    const bucketTrans = translate('field-buckets.bucket');

    if (rootErrors?.['rangeOverlap']?.length > 0) {
      this.formErrorState.set([rootErrors['rangeOverlap'][0]]);
      return;
    }

    if (rootErrors?.['duplicateName']?.length > 0) {
      this.formErrorState.set([rootErrors['duplicateName'][0]]);
      return;
    }

    const controls = this.itemsFormArray.controls;

    for (let i = 0; i < controls.length; i++) {
      const group = controls[i];
      const bucketName = group.get('bucketName')?.value || bucketTrans;

      if (!group.touched && !group.dirty) {
        continue;
      }

      if (
        group.get('bucketName')?.hasError('required') ||
        group.get('bucketName')?.hasError('whitespace')
      ) {
        this.formErrorState.set([
          translate('field-buckets.validation.empty-name', { bucketIndex: i + 1 }),
        ]);
        return;
      }

      if (group.get('bucketName')?.hasError('maxlength')) {
        this.formErrorState.set([
          translate('field-buckets.validation.name-too-long', {
            bucketIndex: i + 1,
            maxLength: 32,
          }),
        ]);
        return;
      }

      if (group.hasError('typeFormat')) {
        this.formErrorState.set([group.getError('typeFormat')]);
        return;
      }

      const minCtrl = group.get('minimumValue');
      const maxCtrl = group.get('maximumValue');

      if (
        (minCtrl?.invalid && (minCtrl.touched || minCtrl.dirty)) ||
        (maxCtrl?.invalid && (maxCtrl.touched || maxCtrl.dirty))
      ) {
        if (minCtrl?.hasError('required') || maxCtrl?.hasError('required')) {
          this.formErrorState.set([
            translate('field-buckets.validation.missing-values', { bucketName }),
          ]);
          return;
        }
      }

      if (group.hasError('minLessThanMax')) {
        this.formErrorState.set([translate('field-buckets.validation.min-greater-than-max')]);
        return;
      }
    }

    return this.formErrorState.set([]);
  }

  // This is only needed b/c Kitchen input currently not emitting valueChange when value === ''
  onNumericValueChange(group, key, value) {
    if (value === '') {
      group.get(key).setValue(null);
    }
  }

  nameDuplicateValidator = (group: AbstractControl): ValidationErrors | null => {
    const formArray = (group as FormGroup).controls['items'] as FormArray;
    const names: string[] = [];

    for (const control of formArray.controls) {
      const val = control.get('bucketName')?.value;
      if (val && typeof val === 'string' && val.trim()) {
        names.push(val.trim());
      }
    }

    const uniqueNames = new Set(names);
    if (uniqueNames.size !== names.length) {
      return { duplicateName: [translate('field-buckets.validation.duplicate-name')] };
    }
    return null;
  };

  rangeOverlapValidator = (group: AbstractControl): ValidationErrors | null => {
    const formArray = (group as FormGroup).controls['items'] as FormArray;
    const errors: string[] = [];
    const buckets = formArray.getRawValue();

    for (let i = 0; i < buckets.length; i++) {
      for (let j = i + 1; j < buckets.length; j++) {
        const a = buckets[i];
        const b = buckets[j];

        if (
          a.minimumValue === null ||
          a.maximumValue === null ||
          b.minimumValue === null ||
          b.maximumValue === null
        ) {
          continue;
        }

        let aIsBeforeB = a.maximumValue < b.minimumValue;
        if (a.maximumValue === b.minimumValue) {
          aIsBeforeB = !a.includeMaximum || !b.includeMinimum;
        }

        let bIsBeforeA = b.maximumValue < a.minimumValue;
        if (b.maximumValue === a.minimumValue) {
          bIsBeforeA = !b.includeMaximum || !a.includeMinimum;
        }

        if (!aIsBeforeB && !bIsBeforeA) {
          const bucketTrans = translate('field-buckets.bucket');
          const nameA = a.bucketName || `${bucketTrans} #${i + 1}`;
          const nameB = b.bucketName || `${bucketTrans} #${j + 1}`;
          errors.push(translate('field-buckets.validation.bucket-overlap', { nameA, nameB }));
        }
      }
    }
    return errors.length > 0 ? { rangeOverlap: errors } : null;
  };

  typeFormatValidator = (group: AbstractControl): ValidationErrors | null => {
    const type = this.expectedType();
    if (!type) {
      return null;
    }

    const minVal = group.get('minimumValue')?.value;
    const maxVal = group.get('maximumValue')?.value;
    const bucketName = group.get('bucketName')?.value || 'Unnamed';

    const isInvalid = (val: any) => {
      if (val === null || val === '') {
        return false;
      }
      if (type === FieldBucketType.INTEGER) {
        return !Number.isInteger(Number(val));
      }
      if (type === FieldBucketType.FLOAT) {
        return isNaN(Number(val));
      }
      return false;
    };

    if (isInvalid(minVal) || isInvalid(maxVal)) {
      return {
        typeFormat: translate('field-buckets.validation.invalid-type', {
          bucketName,
          type: translate(`field-buckets.validation.${type.toLowerCase()}`),
        }),
      };
    }
    return null;
  };

  minLessThanMaxValidator = (group: AbstractControl): ValidationErrors | null => {
    const minVal: number | null | undefined = group.get('minimumValue')?.value;
    const maxVal: number | null | undefined = group.get('maximumValue')?.value;
    if (minVal == null || maxVal == null) {
      return null;
    }

    if (minVal >= maxVal) {
      return { minLessThanMax: true };
    }

    return null;
  };
}
